/**
 * DebugConsolePanel — mobile-friendly GUI console that captures all
 * console.log/warn/error/info output in a scrollable, selectable list.
 *
 * Accessible via the Debug menu when debug mode is enabled.
 * Supports keyboard selection (Shift+click for range, Ctrl/Cmd+click for
 * multi-select) and a touch toolbar for pure touch control.
 */

import { ArrowDownUp, Copy, MousePointer, Trash2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useStore } from "../app/store.ts";
import {
  clearAll,
  getSnapshot,
  type LogEntry,
  type LogLevel,
  subscribe,
} from "../debug/logBuffer.ts";
import { Button } from "./Button.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import { useTransientFeedback } from "./shared.tsx";
import {
  COLOR_DEBUG_OVERLAY_BG,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_TINY,
  RADIUS_DOT,
  RADIUS_XS,
  Z_DEBUG,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${h}:${m}:${s}.${ms}`;
}

const LEVELS: LogLevel[] = ["log", "info", "warn", "error"];

const LEVEL_COLORS: Record<string, string> = {
  log: "#0f0",
  info: "#0af",
  warn: "#ff0",
  error: "#f44",
};

const LEVEL_LABELS: Record<string, string> = {
  log: "Log",
  info: "Info",
  warn: "Warn",
  error: "Err",
};

type TouchMode = "normal" | "select" | "range";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DebugConsolePanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.console);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-console", () => togglePanel("console"));

  const allEntries = useSyncExternalStore(subscribe, getSnapshot);

  // Level filter
  const [enabledLevels, setEnabledLevels] = useState<Set<LogLevel>>(
    () => new Set(LEVELS),
  );
  const toggleLevel = useCallback((level: LogLevel) => {
    setEnabledLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  }, []);

  const entries = useMemo(
    () =>
      enabledLevels.size === LEVELS.length
        ? allEntries
        : allEntries.filter((e) => enabledLevels.has(e.level)),
    [allEntries, enabledLevels],
  );

  // Selection state
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const lastClickedId = useRef<number | null>(null);
  const [touchMode, setTouchMode] = useState<TouchMode>("normal");
  const [copyFlash, fireCopyFlash] = useTransientFeedback(800);

  // Visible entry cap — show last N, with "load more" to expand
  const PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const hiddenCount = Math.max(0, entries.length - visibleCount);
  const visibleEntries = hiddenCount > 0 ? entries.slice(hiddenCount) : entries;

  const loadMore = useCallback(() => {
    setVisibleCount((v) => v + PAGE_SIZE);
  }, []);

  // Auto-scroll
  const scrollRef = useRef<HTMLDivElement>(null);
  const wasAtBottom = useRef(true);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !wasAtBottom.current) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    wasAtBottom.current =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
  }, []);

  // Clear selection when entries are cleared
  useEffect(() => {
    if (entries.length === 0) setSelected(new Set());
  }, [entries.length]);

  // Row click handler
  const onRowClick = useCallback(
    (entry: LogEntry, e: React.MouseEvent) => {
      const isShift = e.shiftKey || touchMode === "range";
      const isCtrl = e.ctrlKey || e.metaKey || touchMode === "select";

      setSelected((prev) => {
        const next = new Set(prev);

        if (isShift && lastClickedId.current != null) {
          // Range select
          const ids = entries.map((en) => en.id);
          const startIdx = ids.indexOf(lastClickedId.current);
          const endIdx = ids.indexOf(entry.id);
          if (startIdx !== -1 && endIdx !== -1) {
            const lo = Math.min(startIdx, endIdx);
            const hi = Math.max(startIdx, endIdx);
            for (let i = lo; i <= hi; i++) next.add(ids[i]);
          }
        } else if (isCtrl) {
          // Toggle individual
          if (next.has(entry.id)) next.delete(entry.id);
          else next.add(entry.id);
        } else {
          // Single select (or deselect if already the only one)
          if (next.size === 1 && next.has(entry.id)) {
            next.clear();
          } else {
            next.clear();
            next.add(entry.id);
          }
        }
        return next;
      });

      lastClickedId.current = entry.id;
    },
    [entries, touchMode],
  );

  // Copy selected
  const copySelected = useCallback(() => {
    const selectedEntries = entries.filter((e) => selected.has(e.id));
    if (selectedEntries.length === 0) return;
    const text = selectedEntries
      .map(
        (e) =>
          `[${e.level.toUpperCase()}] ${formatTime(e.timestamp)} ${e.message}`,
      )
      .join("\n");
    navigator.clipboard.writeText(text).then(fireCopyFlash);
  }, [entries, selected, fireCopyFlash]);

  // Clear all
  const handleClear = useCallback(() => {
    clearAll();
    setSelected(new Set());
    setVisibleCount(PAGE_SIZE);
  }, []);

  if (!debugEnabled || !panelOpen) return null;

  return (
    <DraggablePanel
      id="debug-console"
      defaultPosition={{ bottom: 8, left: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      resizable
      defaultSize={{ width: 420, height: 320 }}
      minSize={{ width: 240, height: 140 }}
      maxSize={{ width: 800, height: 600 }}
      selectionColor="#ff0"
      tint="rgba(0,255,0,0.06)"
      style={{ background: COLOR_DEBUG_OVERLAY_BG }}
      {...sel}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "100%",
            fontFamily: "monospace",
            fontSize: FONT_SIZE_TINY,
          }}
        >
          <span style={{ color: "#0f0", fontWeight: "bold", marginRight: 4 }}>
            CONSOLE
          </span>
          {LEVELS.map((level) => (
            <LevelCheckbox
              key={level}
              level={level}
              checked={enabledLevels.has(level)}
              onToggle={toggleLevel}
            />
          ))}
          <span
            style={{ color: COLOR_TEXT_DIM, fontSize: 9, marginLeft: "auto" }}
          >
            {entries.length !== allEntries.length
              ? `${entries.length}/${allEntries.length}`
              : allEntries.length}
          </span>
        </div>
      }
    >
      <div
        style={{
          background: COLOR_DEBUG_OVERLAY_BG,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          borderRadius: "0 0 6px 6px",
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: "4px 6px",
            borderBottom: "1px solid rgba(0,255,0,0.12)",
            flexShrink: 0,
          }}
        >
          <ToolbarButton
            active={touchMode === "select"}
            onClick={() =>
              setTouchMode((m) => (m === "select" ? "normal" : "select"))
            }
            title="Toggle select mode"
          >
            <MousePointer size={11} />
            <span>Select</span>
          </ToolbarButton>
          <ToolbarButton
            active={touchMode === "range"}
            onClick={() =>
              setTouchMode((m) => (m === "range" ? "normal" : "range"))
            }
            title="Toggle range select mode"
          >
            <ArrowDownUp size={11} />
            <span>Range</span>
          </ToolbarButton>

          <div style={{ flex: 1 }} />

          <ToolbarButton
            active={false}
            disabled={selected.size === 0}
            onClick={copySelected}
            title="Copy selected entries"
            flash={copyFlash}
          >
            <Copy size={11} />
            <span>{copyFlash ? "Copied" : "Copy"}</span>
          </ToolbarButton>
          <ToolbarButton
            active={false}
            disabled={entries.length === 0}
            onClick={handleClear}
            title="Clear all logs"
          >
            <Trash2 size={11} />
            <span>Clear</span>
          </ToolbarButton>
        </div>

        {/* Log list */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            minHeight: 0,
          }}
        >
          {entries.length === 0 ? (
            <div
              style={{
                color: COLOR_TEXT_FAINT,
                fontFamily: "monospace",
                fontSize: FONT_SIZE_TINY,
                padding: "12px 10px",
              }}
            >
              No logs yet…
            </div>
          ) : (
            <>
              {hiddenCount > 0 && (
                <Button
                  data-no-drag
                  onClick={loadMore}
                  variant="ghost"
                  size="xs"
                  fullWidth
                  style={{
                    padding: "4px 10px",
                    borderBottom: "1px solid rgba(0,255,0,0.1)",
                    background: "rgba(0,255,0,0.05)",
                    color: "#0f0",
                    fontFamily: "monospace",
                    fontSize: 9,
                    textAlign: "center",
                  }}
                >
                  {hiddenCount} older {hiddenCount === 1 ? "entry" : "entries"}{" "}
                  — load more
                </Button>
              )}
              {visibleEntries.map((entry) => (
                <LogRow
                  key={entry.id}
                  entry={entry}
                  isSelected={selected.has(entry.id)}
                  onClick={onRowClick}
                />
              ))}
            </>
          )}
        </div>

        {/* Selection count */}
        {selected.size > 0 && (
          <div
            style={{
              padding: "3px 10px",
              borderTop: "1px solid rgba(0,255,0,0.12)",
              fontFamily: "monospace",
              fontSize: 9,
              color: "#0f0",
              opacity: 0.7,
              flexShrink: 0,
            }}
          >
            {selected.size} selected
          </div>
        )}
      </div>
    </DraggablePanel>
  );
}

// ---------------------------------------------------------------------------
// LogRow
// ---------------------------------------------------------------------------

function LogRow({
  entry,
  isSelected,
  onClick,
}: {
  entry: LogEntry;
  isSelected: boolean;
  onClick: (entry: LogEntry, e: React.MouseEvent) => void;
}) {
  const levelColor = LEVEL_COLORS[entry.level] || "#0f0";

  return (
    <div
      onClick={(e) => onClick(entry, e)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 6,
        padding: "2px 10px",
        fontFamily: "monospace",
        fontSize: FONT_SIZE_TINY,
        lineHeight: 1.5,
        cursor: "pointer",
        userSelect: "none",
        WebkitUserSelect: "none",
        background: isSelected ? "rgba(0,255,0,0.12)" : "transparent",
        borderLeft: isSelected ? "2px solid #0f0" : "2px solid transparent",
      }}
    >
      {/* Level dot */}
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: RADIUS_DOT,
          background: levelColor,
          flexShrink: 0,
          marginTop: 5,
        }}
      />
      {/* Timestamp */}
      <span
        style={{
          color: COLOR_TEXT_DIM,
          flexShrink: 0,
          fontSize: 9,
          marginTop: 1,
        }}
      >
        {formatTime(entry.timestamp)}
      </span>
      {/* Message */}
      <span
        style={{
          color: levelColor === "#0f0" ? "#0f0" : levelColor,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        }}
      >
        {entry.message}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LevelCheckbox — compact colored checkbox in the header
// ---------------------------------------------------------------------------

function LevelCheckbox({
  level,
  checked,
  onToggle,
}: {
  level: LogLevel;
  checked: boolean;
  onToggle: (level: LogLevel) => void;
}) {
  const color = LEVEL_COLORS[level];
  return (
    <Button
      data-no-drag
      onClick={() => onToggle(level)}
      title={`${checked ? "Hide" : "Show"} ${level} entries`}
      variant="ghost"
      size="xs"
      style={{
        gap: 3,
        padding: "1px 4px",
        borderRadius: RADIUS_DOT,
        border: `1px solid ${checked ? color : "rgba(255,255,255,0.1)"}`,
        background: checked ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        color: checked ? color : COLOR_TEXT_FAINT,
        fontFamily: "monospace",
        fontSize: 8,
        opacity: checked ? 1 : 0.5,
        lineHeight: 1.3,
      }}
    >
      {LEVEL_LABELS[level]}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// ToolbarButton
// ---------------------------------------------------------------------------

function ToolbarButton({
  active,
  disabled,
  onClick,
  title,
  flash,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  flash?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      data-no-drag
      onClick={onClick}
      disabled={disabled}
      title={title}
      variant="ghost"
      size="xs"
      style={{
        gap: 3,
        padding: "3px 7px",
        borderRadius: RADIUS_XS,
        border:
          "1px solid " +
          (active
            ? "rgba(0,255,0,0.4)"
            : flash
              ? "rgba(0,255,0,0.4)"
              : "rgba(0,255,0,0.15)"),
        background: active
          ? "rgba(0,255,0,0.15)"
          : flash
            ? "rgba(0,255,0,0.1)"
            : "transparent",
        color: disabled ? "rgba(0,255,0,0.25)" : "#0f0",
        fontFamily: "monospace",
        fontSize: 9,
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Button>
  );
}
